"use client";

import { MapPinIcon } from "@heroicons/react/24/outline";
import { searchLocations } from "api/locations/locations";
import { useData } from "data/data-provider";
import { FC, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import ClearDataButton from "./ClearDataButton";
import { locationLabel, locationSubLabel } from "utils/locationLabel";
import LocationDropoffModal from "./LocationDropoffModal";
import { LOCATION_COPY } from "config/locationCopy";
import { renterError } from "utils/errorMessage";

export interface LocationInputProps {
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
  valueFromParent?: string;
  searchBar?: boolean;
  /** Mirror the date fields in the same bar: 'top' = label above the value
   *  (the From/Until bar), 'bottom' = value above the label (the Add-dates bar). */
  labelPosition?: "top" | "bottom";
}

const DropOffLocationInput: FC<LocationInputProps> = ({
  autoFocus = false,
  placeHolder = LOCATION_COPY.dropOff.label,
  desc = LOCATION_COPY.dropOff.prompt,
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
  searchBar,
  labelPosition = "top",
}) => {
  const {
    ourLocation,
    setDropoffLocation,
    searchedDropoffLocation,
    setSearchedDropoffLocation,
    dropOffLocation,
    ourCompany,
  } = useData();
  const [searchedLocations, setSearchedLocations] = useState<any>();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [showPopover, setShowPopover] = useState(autoFocus);

  const callSearchLocationsAPI = async (input: string) => {
    try {
      const data = (await searchLocations(input)) as any;
      setSearchedLocations(data.predictions);
    } catch (error: any) {
      toast.error(renterError(error, "locations"));
    }
  };

  useEffect(() => {
    callSearchLocationsAPI(searchedDropoffLocation);
  }, [searchedDropoffLocation]);

  useEffect(() => {
    setShowPopover(autoFocus);
  }, [autoFocus]);

  useEffect(() => {
    if (eventClickOutsideDiv) {
      document.removeEventListener("click", eventClickOutsideDiv);
    }
    showPopover && document.addEventListener("click", eventClickOutsideDiv);
    return () => {
      document.removeEventListener("click", eventClickOutsideDiv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopover]);

  useEffect(() => {
    if (showPopover && inputRef.current) {
      inputRef.current.focus({ preventScroll: true }); // iOS Safari scrolls the page to the top when focusing inside Design B's transformed wrappers
    }
  }, [showPopover]);

  const eventClickOutsideDiv = (event: MouseEvent) => {
    if (!containerRef.current) return;
    // CLICK IN_SIDE
    if (!showPopover || containerRef.current.contains(event.target as Node)) {
      return;
    }
    // CLICK OUT_SIDE
    setShowPopover(false);
  };

  useEffect(() => {
    if (dropOffLocation === null) {
      setSearchedDropoffLocation(""); // Clear state
      if (inputRef.current) {
        inputRef.current.value = ""; // Clear the input field display
      }
    }
  }, [dropOffLocation, setSearchedDropoffLocation]);

  const handleSelectLocation = (item: any) => {
    setSearchedDropoffLocation("");
    setDropoffLocation(null);

    if (item.radius) {
      setSelectedLocation(item);
      setShowLocationModal(true);
      setShowPopover(false);
    } else {
      setDropoffLocation(item);
      setShowPopover(false);
    }
  };

  const renderRecentSearches = () => {
    return (
      <>
        <div className="rounded-2xl border border-secondary-100 bg-bg-color shadow-2xl shadow-text-color/10 ring-1 ring-text-color/5 overflow-hidden p-1.5">
          {ourLocation
            .filter((item) => item.active !== false)
            .sort((a, b) => a.price - b.price)
            .map((item: any) => (
              <div
                key={item.id}
                onClick={() => handleSelectLocation(item)}
                className="group/loc flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 cursor-pointer hover:bg-secondary-50 rounded-xl transition-colors text-text-color"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-brand/15 ring-1 ring-primary-brand/30 transition-colors group-hover/loc:bg-primary-brand/30 group-hover/loc:ring-primary-brand/60">
                    <MapPinIcon className="h-4 w-4 text-primary-brand" />
                  </span>
                  <span className="truncate text-base font-semibold">{item.name}</span>
                </div>
                <span
                  className="shrink-0 inline-flex items-center rounded-full bg-secondary-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-text-color/80"
                  style={{ minWidth: 72, justifyContent: "center" }}
                >
                  {item.price === 0 ? "Free" : `$${item.price}/trip`}
                </span>
              </div>
            ))}
        </div>
      </>
    );
  };

  const handleClear = () => {
    setSearchedDropoffLocation("");
    setDropoffLocation(null);
    setShowPopover(false);
    if (inputRef.current) {
      inputRef.current.value = ""; // Reset the input field's display
    }
  };

  return (
    <div className={`relative flex ${className}`} ref={containerRef}>
      <div
        onClick={() => setShowPopover(true)}
        className={`flex z-10 flex-1 relative [ nc-hero-field-padding ] flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left  ${
          showPopover ? "nc-hero-field-focused !rounded-md" : ""
        }`}
      >
        {/* Lead with an icon, matching the date and time fields. */}
        <div className="text-text-color">
          <MapPinIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        {/* Short label on top, value underneath — matching the date and time
            fields beside it. */}
        <div className="flex-grow min-w-0">
          {labelPosition === "top" && (
            <p className="text-secondary-text-color font-semibold">{placeHolder}</p>
          )}
          <div className="relative w-full flex items-center">
            <input
              className={`block w-full pr-10 ${
                searchBar === false ? "text-text-color" : "text-text-color"
              } bg-transparent cursor-pointer border-none focus:ring-0 p-0 focus:outline-none ${
                searchBar === false
                  ? "focus:placeholder-text-color"
                  : "focus:placeholder-text-color"
              } xl:text-lg ${labelPosition === "top" ? "font-medium" : "font-semibold"} ${
                searchBar === false ? "" : "placeholder-text-color"
              }  truncate `}
              placeholder={desc}
              value={
                searchedDropoffLocation.length > 0
                  ? searchedDropoffLocation
                  : [
                      locationLabel(dropOffLocation, ourCompany?.company_name),
                      locationSubLabel(dropOffLocation, ourCompany?.company_name),
                    ]
                      .filter(Boolean)
                      .join(" · ")
              }
              autoFocus={showPopover}
              onChange={(e) => {
                setSearchedDropoffLocation(e.currentTarget.value);
              }}
              ref={inputRef}
              readOnly
            />
            {(searchedDropoffLocation || dropOffLocation) &&
              ourLocation.filter((loc) => loc.active !== false).length > 1 && (
                <ClearDataButton onClick={handleClear} />
              )}
          </div>
          {labelPosition === "bottom" && (
            <span className="block mt-1 text-sm text-text-color leading-none font-light truncate">
              {placeHolder}
            </span>
          )}
        </div>
      </div>

      {showPopover && (
        <div
          className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-0  ${divHideVerticalLineClass}`}
        ></div>
      )}

      {showPopover &&
        ((searchedDropoffLocation && searchedLocations?.length > 0) ||
          ourLocation.filter((item) => item.active === true).length > 0) && (
          <div className="absolute overflow-y-auto left-0 z-40 w-full rounded-lg sm:min-w-[400px] top-full mt-3 max-h-96">
            {renderRecentSearches()}
          </div>
        )}
      <LocationDropoffModal
        isOpen={showLocationModal}
        selectedLocation={selectedLocation}
        onClose={() => setShowLocationModal(false)}
        ourCompany={ourCompany}
        setSearchedDropoffLocation={setSearchedDropoffLocation}
        setDropoffLocation={setDropoffLocation}
        ourLocation={ourLocation}
      />
    </div>
  );
};

export default DropOffLocationInput;
